import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  addRole,
  updateRole,
  getRoleById,
} from "../../services/roleService";

export const AddRoleLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    role_name: "",
    description: "",
    status: "active",
    permissions: {
      user_management: false,
      team_management: false,
      lead_management: false,
      reports: false,
      live_calls: false,
      billing: false,
      dashboard: false,
      settings: false,
    },
  });

  /* LOAD ROLE (EDIT MODE) */
  useEffect(() => {
    if (isEdit) loadRole();
  }, [id]);

  const loadRole = async () => {
    try {
      const res = await getRoleById(id);
      if (res.status === "success") {
        setForm(res.data);
      }
    } catch {
      toast.error("Failed to load role");
    }
  };

  /* HANDLE INPUT */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  /* HANDLE PERMISSION */
  const handlePermission = (key) => {
    setForm({
      ...form,
      permissions: {
        ...form.permissions,
        [key]: !form.permissions[key],
      },
    });
  };

  /* VALIDATION */
  const validate = () => {
    let err = {};
    if (!form.role_name) err.role_name = "Role name required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* SUBMIT */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const res = isEdit
        ? await updateRole(id, form)
        : await addRole(form);

      if (res.status === "success") {
        toast.success(isEdit ? "Role updated" : "Role created");
        navigate("/roles");
      }
    } catch {
      toast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5>{isEdit ? "Edit Role" : "Add Role"}</h5>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card-body">
          {/* ROLE NAME */}
          <div className="mb-3">
            <label className="form-label">Role Name *</label>
            <input
              type="text"
              name="role_name"
              className={`form-control ${
                errors.role_name ? "is-invalid" : ""
              }`}
              value={form.role_name}
              onChange={handleChange}
            />
            {errors.role_name && (
              <div className="invalid-feedback">{errors.role_name}</div>
            )}
          </div>

          {/* DESCRIPTION */}
          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              className="form-control"
              rows="3"
              value={form.description}
              onChange={handleChange}
            />
          </div>

          {/* STATUS */}
          <div className="mb-3">
            <label className="form-label">Status</label>
            <select
              name="status"
              className="form-select"
              value={form.status}
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* PERMISSIONS */}
          <div className="mb-3">
            <label className="form-label fw-bold">Permissions</label>
            <div className="row">
              {Object.keys(form.permissions).map((key) => (
                <div className="col-md-3 mb-2" key={key}>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={form.permissions[key]}
                      onChange={() => handlePermission(key)}
                    />
                    <label className="form-check-label text-capitalize">
                      {key.replace("_", " ")}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="card-footer text-end">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Role"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddRoleLayer;
